/**
 * Active draft order — sessionStorage-backed singleton.
 *
 * The multi-item cart used to rely entirely on React Router's `location.state`
 * to thread the draft `orderId` between QuoteCalculator → place_order →
 * ReviewOrderPage → "Add new item" → QuoteCalculator → place_order →
 * ReviewOrderPage. That state is fragile: any hard refresh, top-nav click,
 * or unrelated `navigate()` resets it, and the user silently loses items.
 *
 * Mirroring the active draft id in sessionStorage makes the cart survive:
 *   - Hard refresh of any page in the wizard
 *   - Side-trips to /products / /splash / /dashboard and back
 *   - Closing & reopening the same tab? — NO (sessionStorage is per-tab,
 *     scoped to the lifetime of the tab; that's intentional. Real
 *     long-lived "abandoned cart" recovery is a future enhancement that
 *     should hit the backend draft list, not localStorage.)
 *
 * Cleared on:
 *   - Successful order submission (status → proof_pending / submitted)
 *   - User logout
 *   - Customer explicitly resets the cart via a "Start fresh" CTA
 *     (no UI yet — backlog item)
 */
import { log } from './logger';

const KEY = 'eg3_active_draft_order_id';
// Custom event name dispatched on every set/clear so React components can
// reactively re-read the value without a setInterval poll.
const EVENT = 'eg3:active-draft-changed';

const fire = () => {
  try {
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch (err) {
    // SSR / no-window — non-fatal; hooks just won't reactively re-pull.
    log.debug('[activeDraftOrder] dispatchEvent failed:', err?.name || err);
  }
};

export const ACTIVE_DRAFT_EVENT = EVENT;

export const getActiveDraftOrderId = () => {
  try {
    return window.sessionStorage.getItem(KEY) || null;
  } catch (err) {
    log.debug('[activeDraftOrder] sessionStorage.getItem failed:', err?.name || err);
    return null;
  }
};

export const setActiveDraftOrderId = (orderId) => {
  if (!orderId) return;
  try {
    const prev = window.sessionStorage.getItem(KEY);
    window.sessionStorage.setItem(KEY, String(orderId));
    if (prev !== String(orderId)) fire();
  } catch (err) {
    // Private mode / quota exceeded — draft id won't survive refresh
    // but the in-memory React state still keeps the cart together for
    // this navigation cycle.
    log.debug('[activeDraftOrder] sessionStorage.setItem failed:', err?.name || err);
  }
};

export const clearActiveDraftOrderId = () => {
  try {
    const prev = window.sessionStorage.getItem(KEY);
    window.sessionStorage.removeItem(KEY);
    if (prev) fire();
  } catch (err) {
    log.debug('[activeDraftOrder] sessionStorage.removeItem failed:', err?.name || err);
  }
};

/** Fire the change event manually — useful when item count changes
 *  on the same draft (add/remove item) without flipping the id. */
export const notifyActiveDraftChanged = fire;
